// Pricing helpers.
//
// ACL-compliant sale model: a strikethrough "was" price is shown ONLY while a
// genuine, dated sale window is active (now between saleStartsAt and saleEndsAt
// AND rrpPrice set AND rrpPrice above the current price). Outside a window the
// product shows a single price - no strikethrough, no discount badge. There is
// deliberately NO permanent auto-calculated discount (that would be an
// open-ended "promotion", which ACL does not allow).

export function fmtPrice(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  return Number(n).toLocaleString('en-AU', { maximumFractionDigits: 0 });
}

// Sale-window state for a product at a given moment.
export function getSaleState(product, now = Date.now()) {
  if (!product) return { active: false, rrp: null, end: null, label: null };
  const start = product.saleStartsAt ? Date.parse(product.saleStartsAt) : null;
  const end = product.saleEndsAt ? Date.parse(product.saleEndsAt) : null;
  const rrp = (typeof product.rrpPrice === 'number' && product.rrpPrice > 0) ? product.rrpPrice : null;
  const active = rrp != null
    && start != null && !Number.isNaN(start)
    && end != null && !Number.isNaN(end)
    && now >= start && now <= end;
  return { active, rrp, end, label: (product.saleLabel && String(product.saleLabel).trim()) || null };
}

// Find the cheapest variant with a real price. Returns the variant object or null.
export function cheapestVariant(product) {
  if (!product || !Array.isArray(product.variant_prices) || product.variant_prices.length === 0) {
    return null;
  }
  const priced = product.variant_prices.filter((v) => v && typeof v.price === 'number' && v.price > 0);
  if (!priced.length) return null;
  return priced.reduce((min, v) => (v.price < min.price ? v : min), priced[0]);
}

// { sale, original, discountPct, saleActive, saleEndsAt, saleLabel } for a product.
// `sale` is the current price shown/charged; `original` is the struck-through
// "was" price, and is non-null ONLY during an active sale window. Pass
// `explicitSale` (e.g. a selected size on the detail page) to price that variant.
export function getPricing(product, explicitSale, now = Date.now()) {
  if (explicitSale == null && !product) return null;
  const { active, rrp, end, label } = getSaleState(product, now);
  const variant = product ? cheapestVariant(product) : null;

  let current;
  if (explicitSale != null) {
    current = explicitSale;
  } else if (variant) {
    // Variant-priced product (awnings): the cheapest variant is the "From" price.
    current = variant.price;
  } else if (active && product && product.salePrice != null) {
    // Single-price product, on sale: charge the promo price.
    current = product.salePrice;
  } else if (product && product.price_from != null) {
    // Single-price product, normal: the regular price is rrp when set, else price_from.
    current = (rrp != null) ? rrp : product.price_from;
  } else if (rrp != null) {
    current = rrp;
  }
  if (current == null) return null;

  // Strikethrough only during an active window, and only if the "was" is genuinely higher.
  const original = active && rrp != null && rrp > current ? rrp : null;
  const discountPct = original ? Math.round(((original - current) / original) * 100) : 0;

  return {
    sale: current,
    original,
    discountPct,
    saleActive: active && original != null,
    saleEndsAt: (end != null && !Number.isNaN(end)) ? new Date(end) : null,
    saleLabel: label,
  };
}

// "Sale ends 5 August 2026" - Australian English long date.
export function formatSaleEnds(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return `Sale ends ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}
